import type { Analytics, AnalyticsSettings } from 'firebase/analytics';
import type { FirebaseApp, FirebaseOptions } from 'firebase/app';
import type { FirebasePerformance, PerformanceSettings } from 'firebase/performance';

import type { AuthContainer } from './FirebaseAuthContainer.js';
import type { FirebasePlatform } from './firebase-platform.js';
import type { FirestoreConfig, FirestoreContainer } from './FirestoreContainer.js';
import type { FirebaseStorageConfig, StorageContainer } from './StorageContainer.js';

import { setLogLevel, setUserLogHandler } from '@firebase/logger';
import { initializeApp } from 'firebase/app';

import { createContainer } from '@elara/core/di';

import { createAuthContainer } from './FirebaseAuthContainer.js';
import { createFirestoreContainer } from './FirestoreContainer.js';
import { createStorageContainer } from './StorageContainer.js';

export type Bind = Partial<{ host: string; port: number }>;
export type FlexibleEmulatorsConfig =
  | {
      /** Whether to enable the storage emulator. Enabled by default if emulators are enabled. */
      storage?: EmulatorsConfig['storage'] | false;
      /** Whether to enable the firestore emulator. Enabled by default if emulators are enabled. */
      firestore?: EmulatorsConfig['firestore'] | false;
      /** Whether to enable the auth emulator. Enabled by default if emulators are enabled. */
      auth?: EmulatorsConfig['auth'] | false;
    }
  | boolean;

export interface EmulatorsConfig {
  storage?: Bind;
  firestore?: Bind;
  auth?: Bind & { disableWarnings?: boolean };
}

export interface FirebaseConfig {
  app: FirebaseOptions;
  performance?: PerformanceSettings;
  storage?: FirebaseStorageConfig;
  firestore?: FirestoreConfig;
  analytics?: AnalyticsSettings;
  emulators?: FlexibleEmulatorsConfig;
  insaneDebug?: boolean;
}

const defaultEmulators = {
  storage: { host: 'localhost', port: 9199 },
  firestore: { host: 'localhost', port: 8080 },
  auth: { host: 'localhost', port: 9099, disableWarnings: true },
} as const satisfies FlexibleEmulatorsConfig;

/**
 * Composes the Firebase container. Platform-divergent services (auth persistence, the web-only
 * analytics/performance modules) arrive through the injected {@link FirebasePlatform} so this module
 * stays free of web-only imports that would crash the React Native (Metro) bundle. See ELARA-689.
 */
export function createFirebaseContainer(config: FirebaseConfig, platform: FirebasePlatform) {
  console.log('Creating firebase container with config:', config);

  const $firebase = createContainer('firebase')
    .provided('config', { ...config, emulators: normalizeEmulatorConfig(config.emulators) } as const)
    .dependent('app', ['config'], ({ config }): FirebaseApp => {
      const app = initializeApp(config.app);
      setLogLevel(config?.insaneDebug ? 'debug' : 'verbose');
      setUserLogHandler(({ message, level }) => {
        if (level === 'silent') return;
        const browserLevel = level === 'verbose' ? 'debug' : level;
        console[browserLevel](message);
      });

      return app;
    })
    .dependent('performance', ['app', 'config'], ({ app, config }): FirebasePerformance | undefined => {
      // Optional per platform (ELARA-689): web injects `initPerformance`; React Native omits it
      // (`firebase/performance` is web-only). The DI `.use` resolves EVERY provider eagerly, so this
      // must yield `undefined` rather than throw — a throw would crash composition on RN, where this
      // service is legitimately absent. Resolved for its init side-effect; nothing reads the value.
      return platform.initPerformance?.(app, config.performance);
    })
    .dependent('firestore', ['app'], ({ app }): FirestoreContainer => {
      return platform.createFirestore
        ? platform.createFirestore(app, config.firestore)
        : createFirestoreContainer(app, config.firestore).use;
    })
    // Auth and storage are resolved as plain container *use*-objects rather than `.nested(...)`
    // sub-containers, because on React Native the implementation is a different SDK's container
    // altogether (ELARA-982) and only the resolved contract is common to both.
    .dependent('storage', ['app', 'config'], ({ app, config }): StorageContainer => {
      return platform.createStorage
        ? platform.createStorage(app, config.storage)
        : createStorageContainer(app, config.storage).use;
    })
    .dependent('fbAnalytics', ['app', 'config'], ({ app, config }): Analytics | undefined => {
      // Optional per platform (ELARA-689): web-only, `undefined` on React Native (see `performance`
      // above for why this yields instead of throwing under eager `.use` resolution).
      return platform.initAnalytics?.(app, config.analytics);
    })
    .dependent('auth', ['app'], ({ app }): AuthContainer => {
      return platform.createAuth ? platform.createAuth(app) : createAuthContainer(app, platform.createAuthBackend).use;
    })
    .procedure(
      'startEmulators',
      ['config', 'storage', 'firestore', 'auth'],
      ({ config: { emulators }, storage, firestore, auth }): void => {
        console.log('Connecting to emulators:', emulators);
        if (emulators?.storage) {
          const { host, port } = emulators.storage;
          console.log('Connecting to storage emulator:', emulators.storage);
          storage.connectEmulator(host, port);
        }
        if (emulators?.firestore) {
          const { host, port } = emulators.firestore;
          console.log('Connecting to firestore emulator:', emulators.firestore);
          firestore.connectEmulator(host, port);
        }
        if (emulators?.auth) {
          const { host, port, disableWarnings } = emulators.auth;
          console.log('Connecting to auth emulator:', emulators.auth);
          // Delegated to the container so each SDK connects its own emulator (ELARA-982).
          auth.connectEmulator(`http://${host}:${port}`, { disableWarnings });
        }
      },
    );
  return $firebase;
}

export type $FirebaseContainer = ReturnType<typeof createFirebaseContainer>;
export type FirebaseContainer = $FirebaseContainer['use'];

/**
 * Normalizes the emulator configuration (gets rid of boolean values)
 * @param emulators - The emulator configuration.
 * @returns The normalized emulator configuration.
 */
function normalizeEmulatorConfig(emulators: FlexibleEmulatorsConfig = false) {
  if (emulators === false) return;
  if (emulators === true) return defaultEmulators;
  return {
    storage: emulators.storage ? ({ ...defaultEmulators.storage, ...emulators.storage } as const) : undefined,
    firestore: emulators.firestore ? ({ ...defaultEmulators.firestore, ...emulators.firestore } as const) : undefined,
    auth: emulators.auth ? ({ ...defaultEmulators.auth, ...emulators.auth } as const) : undefined,
  } satisfies EmulatorsConfig;
}
